import * as React from "react";
import useOnclickOutside from "react-cool-onclickoutside";
import { PlacesType, Tooltip as ReactTooltip } from "react-tooltip";
import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { useToggleButton } from "@react-aria/button";
import { useHover } from "@react-aria/interactions";
import { mergeProps } from "@react-aria/utils";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { useToggleState } from "@react-stately/toggle";
import { v4 } from "uuid";
import { cx } from "@nju/css";
import { Markdown } from "../Markdown";

export interface ITooltipProps {
  content: string | undefined;
  placement?: PlacesType;
  mode?: "markdown" | "raw";
  width?: number;
  clickable?: boolean;
  tooltipBgColor?: string;
  tooltipTextColor?: string;
}

function TooltipComponent({
  children,
  content,
  placement = "right",
  mode = "raw",
  width,
  tooltipBgColor = "!bg-geyser",
  tooltipTextColor = "!text-daintree",
  clickable = false,
}: ITooltipProps & JSX.IntrinsicElements["svg"]) {
  const [isHover, setIsHover] = React.useState(false);
  const state = useToggleState();
  const generatedTooltipId = `tooltip-${v4()}`;

  const { refs } = useFloating({
    placement,
    middleware: [
      shift({ padding: 5 }),
      flip({
        fallbackPlacements: ["top", "bottom", "left"],
      }),
      offset(10),
    ],
  });

  const handleCloseTooltip = () => {
    setIsHover(false);
    state.setSelected(false);
  };

  const { buttonProps } = useToggleButton(
    {
      onFocus: () => {
        state.setSelected(true);
      },
      onBlur: handleCloseTooltip,
      onKeyDown: ({ code }) => {
        if (code === "Escape") {
          handleCloseTooltip();
        }
        if (code === "Tab") {
          state.setSelected(true);
        }
      },
    },
    state,
    // @ts-expect-error it's ok
    refs.reference,
  );

  const { hoverProps } = useHover({
    onHoverEnd: () => {
      if (clickable) {
        return;
      }

      handleCloseTooltip();
    },
    onHoverStart: () => {
      state.setSelected(true);
    },
  });

  const refClickOutside = useOnclickOutside(() => {
    if (!clickable || !state.isSelected || isHover) {
      return;
    }

    handleCloseTooltip();
  });

  const handleOnHoverTooltipContent = () => {
    setIsHover(true);
  };

  return (
    <>
      <button
        className="inline-flex relative bg-transparent border-none outline-none"
        id={generatedTooltipId}
        {...mergeProps(buttonProps, hoverProps)}
        ref={refClickOutside}
      >
        <VisuallyHidden>Zobacz szczegóły</VisuallyHidden>
        {children}
      </button>
      <ReactTooltip
        anchorSelect={`#${generatedTooltipId}`}
        className={cx(
          `${tooltipBgColor} ${tooltipTextColor} !rounded-[12px] !text-size-14 !leading-21 !p-0 z-tooltip`,
          width ? `w-[${width}px]` : "only-mobile:!w-[270px] !w-[300px]",
        )}
        clickable={clickable}
        isOpen={state.isSelected}
        opacity={1}
        place={placement}
      >
        <div
          className="px-4 py-3"
          onMouseEnter={handleOnHoverTooltipContent}
          onMouseLeave={handleCloseTooltip}
        >
          {mode === "markdown" && <Markdown content={content as string} />}
          {mode === "raw" && <span>{content}</span>}
        </div>
      </ReactTooltip>
    </>
  );
}

export function Tooltip(props: React.PropsWithChildren<ITooltipProps>) {
  const [ready, setIsReady] = React.useState(false);
  React.useEffect(() => {
    setIsReady(true);
  }, []);

  if (!ready) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }
  return <TooltipComponent {...props} />;
}
